import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { timeLookupsAPI, timeProfilesAPI, timeReportsAPI } from '../services/api';
import { TimeEntry, TimeProfile, TimeProjectLookup } from '../types';
import { formatHours, todayIso } from '../utils';
import TableLoadingRow from './TableLoadingRow';

interface DayoffSummary {
  profile: TimeProfile;
  thisMonthMinutes: number;
  thisYearMinutes: number;
  totalMinutes: number;
}

const REPORT_START_DATE = '2000-01-01';
const PAGE_SIZE = 500;

const getEntryDate = (entry: TimeEntry) => entry.workDate.slice(0, 10);

const fallbackProfile = (entry: TimeEntry): TimeProfile => ({
  id: entry.timeProfileId,
  displayName: entry.timeProfileName || 'Unknown member',
  email: '',
  role: 'User',
  isActive: true,
  ignoreForWarnings: false,
  expectedWeeklyHours: 0,
  createdDate: '',
});

const buildDayoffSummaries = (
  profiles: TimeProfile[],
  dayoffEntries: TimeEntry[],
  currentMonth: string,
  currentYear: string
) => {
  const summaries = new Map<number, DayoffSummary>();

  profiles.forEach(profile => {
    summaries.set(profile.id, {
      profile,
      thisMonthMinutes: 0,
      thisYearMinutes: 0,
      totalMinutes: 0,
    });
  });

  dayoffEntries.forEach(entry => {
    const existing = summaries.get(entry.timeProfileId);
    const summary = existing || {
      profile: fallbackProfile(entry),
      thisMonthMinutes: 0,
      thisYearMinutes: 0,
      totalMinutes: 0,
    };
    const entryDate = getEntryDate(entry);

    summary.totalMinutes += entry.durationMinutes;
    if (entryDate.startsWith(currentYear)) {
      summary.thisYearMinutes += entry.durationMinutes;
    }
    if (entryDate.startsWith(currentMonth)) {
      summary.thisMonthMinutes += entry.durationMinutes;
    }

    summaries.set(entry.timeProfileId, summary);
  });

  return Array.from(summaries.values())
    .sort((a, b) => b.totalMinutes - a.totalMinutes || a.profile.displayName.localeCompare(b.profile.displayName));
};

const DayoffsPage: React.FC = () => {
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const currentDate = todayIso();
  const currentMonth = currentDate.slice(0, 7);
  const currentYear = currentDate.slice(0, 4);

  useEffect(() => {
    let isMounted = true;

    const loadAllEntries = async () => {
      const allEntries: TimeEntry[] = [];
      let skip = 0;
      let totalCount = Number.POSITIVE_INFINITY;

      while (skip < totalCount) {
        const page = await timeReportsAPI.getDetailed({
          startDate: REPORT_START_DATE,
          endDate: todayIso(),
          skip,
          take: PAGE_SIZE,
        });
        allEntries.push(...page.items);
        totalCount = page.totalCount;
        skip += page.take || PAGE_SIZE;

        if (page.items.length === 0) {
          break;
        }
      }

      return allEntries;
    };

    const loadData = async () => {
      setLoading(true);
      try {
        const [profileData, projectData, entryData] = await Promise.all([
          timeProfilesAPI.getProfiles(true),
          timeLookupsAPI.getProjects(),
          loadAllEntries(),
        ]);

        if (!isMounted) {
          return;
        }

        setProfiles(profileData);
        setProjects(projectData);
        setEntries(entryData);
      } catch {
        if (isMounted) {
          toast.error('Dayoffs could not be loaded');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  const dayoffProjectIds = useMemo(
    () => new Set(projects.filter(project => project.isDayOffProject).map(project => project.id)),
    [projects]
  );
  const dayoffEntries = useMemo(
    () => entries.filter(entry => typeof entry.projectId === 'number' && dayoffProjectIds.has(entry.projectId)),
    [dayoffProjectIds, entries]
  );
  const summaries = useMemo(
    () => buildDayoffSummaries(profiles, dayoffEntries, currentMonth, currentYear),
    [profiles, dayoffEntries, currentMonth, currentYear]
  );
  const totals = useMemo(() => summaries.reduce(
    (acc, summary) => ({
      thisMonthMinutes: acc.thisMonthMinutes + summary.thisMonthMinutes,
      thisYearMinutes: acc.thisYearMinutes + summary.thisYearMinutes,
      totalMinutes: acc.totalMinutes + summary.totalMinutes,
    }),
    { thisMonthMinutes: 0, thisYearMinutes: 0, totalMinutes: 0 }
  ), [summaries]);

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Dayoffs</h1>
          <p>Total time from projects marked as Day offs</p>
        </div>
      </div>

      <div className="stat-grid member-time-stat-grid">
        <div className="stat-card">
          <span>This month</span>
          <strong>{formatHours(totals.thisMonthMinutes / 60)}</strong>
        </div>
        <div className="stat-card">
          <span>This year</span>
          <strong>{formatHours(totals.thisYearMinutes / 60)}</strong>
        </div>
        <div className="stat-card">
          <span>Total</span>
          <strong>{formatHours(totals.totalMinutes / 60)}</strong>
        </div>
      </div>

      <section className="card">
        <div className="card-body">
          <div className="section-heading">
            <div>
              <h2 className="section-title">Dayoffs by member</h2>
              <p className="section-subtitle">Includes only time entries assigned to projects marked as Day offs.</p>
            </div>
          </div>
          <div className="table-responsive member-time-table-wrap">
            <table className="table member-time-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>This month</th>
                  <th>This year</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {loading && <TableLoadingRow colSpan={4} label="Loading dayoffs..." />}
                {!loading && summaries.map(summary => (
                  <tr key={summary.profile.id}>
                    <td data-label="Member">
                      <div className="member-time-name">{summary.profile.displayName}</div>
                      <div className="member-time-email">{summary.profile.email || 'No email'}</div>
                    </td>
                    <td data-label="This month">{formatHours(summary.thisMonthMinutes / 60)}</td>
                    <td data-label="This year">{formatHours(summary.thisYearMinutes / 60)}</td>
                    <td data-label="Total">{formatHours(summary.totalMinutes / 60)}</td>
                  </tr>
                ))}
                {!loading && summaries.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <div className="empty-state compact">No dayoff project time entries found</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DayoffsPage;
